import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { reportError } from '../../lib/sentry';

import { IUser } from '../../lib/interfaces';
import { GENERAL_FONT } from '../../constants/theme';
import { profileBoxStyle } from '../PatientDetails/PatientDetails';
import {
  fetchTreatmentEffect,
  TreatmentEffectParams,
  TreatmentEffectResponse,
  EffectWindow,
} from '../../lib/services/treatmentEffectService';
import {
  fetchPatientTreatmentPlans,
  PatientTreatmentPlanDto,
  TreatmentPlanStatus,
} from '../../lib/services/treatmentPlanService';

import {
  fetchTreatments,
  TreatmentDto,
} from '../../lib/services/treatmentsService';

import CombinedCopyToEmrButton from './CombinedCopyToEmrButton';

type TreatmentPlan = Omit<
  PatientTreatmentPlanDto,
  'patientId' | 'startDate'
> & {
  startDate: string;
};

type ApiError = {
  response?: { data?: { message?: string } };
  message?: string;
};

function pct(n: number) {
  if (!Number.isFinite(n)) return '0%';
  return `${Math.round(n)}%`;
}

function num1(n: number) {
  if (!Number.isFinite(n)) return '0.0';
  return n.toFixed(1);
}

function coverageColor(c: number) {
  if (c >= 0.7) return 'success';
  if (c >= 0.4) return 'warning';
  return 'error';
}

function statusSv(status: TreatmentPlanStatus | 'active' | 'stopped') {
  if (status === 'active') return 'Aktiv';
  if (status === 'draft') return 'Utkast';
  return 'Avslutad';
}

function statusChipSv(status: TreatmentPlanStatus | 'active' | 'stopped') {
  const label = statusSv(status);
  const isActive = status === 'active';
  return (
    <Chip
      size="small"
      label={label}
      color={isActive ? 'success' : 'default'}
      variant={isActive ? 'filled' : 'outlined'}
    />
  );
}

function formatRange(start: string, end: string) {
  return `${start} – ${end}`;
}

function findTreatment(catalog: TreatmentDto[], treatmentId: string) {
  return (catalog || []).find((t) => t.treatmentId === treatmentId);
}

function weeksDataAvailable(diaryDays: number) {
  return Math.floor(diaryDays / 7);
}

function isYmd(s: unknown): s is string {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function normalizePlans(plansArr: PatientTreatmentPlanDto[]): TreatmentPlan[] {
  if (!Array.isArray(plansArr)) return [];

  const out: TreatmentPlan[] = plansArr
    .map((p) => {
      const id = String(p?.id ?? '');
      const treatmentId = String(p?.treatmentId ?? '');
      const status = String(p?.status ?? '') as TreatmentPlanStatus;

      const startDate = p?.startDate ?? null;
      const endDate = p?.endDate ?? null;

      if (!id) return null;
      if (!treatmentId) return null;
      if (status !== 'draft' && status !== 'active' && status !== 'stopped')
        return null;
      if (!isYmd(startDate)) return null;

      return {
        id,
        treatmentId,
        status,
        startDate,
        endDate: isYmd(endDate) ? endDate : null,
      } as TreatmentPlan;
    })
    .filter(Boolean) as TreatmentPlan[];

  const evaluatable = out.filter(
    (p) => p.status === 'active' || p.status === 'stopped'
  );

  return evaluatable
    .slice()
    .sort(
      (a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf()
    );
}

const TreatmentEffectDashboard: React.FC<{ patient: IUser }> = ({
  patient,
}) => {
  const { t } = useTranslation();

  type WindowSnapshot = {
    overrideApplied: boolean;
    overrideOpen: boolean;
    beforeStart: Dayjs | null;
    beforeEnd: Dayjs | null;
    afterStart: Dayjs | null;
    afterEnd: Dayjs | null;
  };

  const [normalWindows, setNormalWindows] = useState<WindowSnapshot | null>(
    null
  );

  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [treatmentCatalog, setTreatmentCatalog] = useState<TreatmentDto[]>([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [treatmentsError, setTreatmentsError] = useState<string | null>(null);

  const [effect, setEffect] = useState<TreatmentEffectResponse | null>(null);
  const [effectLoading, setEffectLoading] = useState(false);
  const [effectError, setEffectError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setTreatmentsLoading(true);
      setTreatmentsError(null);

      try {
        const resp = await fetchTreatments();
        const list = resp.data.result.treatments || [];
        if (!cancelled) setTreatmentCatalog(list);
      } catch (e: unknown) {
        reportError(e);
        if (!cancelled) {
          const error = e as ApiError;
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            t('treatmentEffect.treatmentsFailedToLoad');
          setTreatmentsError(errorMessage);
          setTreatmentCatalog([]);
        }
      } finally {
        if (!cancelled) setTreatmentsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!patient?._id) return;

      setPlansLoading(true);
      setPlansError(null);

      try {
        const resp = await fetchPatientTreatmentPlans(patient._id);
        const plansData = resp.data.result.plans || [];
        if (!cancelled) setPlans(normalizePlans(plansData));
      } catch (e: unknown) {
        reportError(e);
        if (!cancelled) {
          const error = e as ApiError;
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            t('treatmentEffect.failedToLoad');
          setPlansError(errorMessage);
          setPlans([]);
        }
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [patient?._id, t]);

  const defaultSelectedPlanId = useMemo(() => {
    const activePlans = plans
      .filter((p) => p.status === 'active')
      .slice()
      .sort(
        (a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf()
      );

    if (activePlans.length > 0) return activePlans[0].id;
    return plans[0]?.id ?? '';
  }, [plans]);

  const [selectedPlanId, setSelectedPlanId] = useState(defaultSelectedPlanId);

  useEffect(() => {
    setSelectedPlanId(defaultSelectedPlanId);
  }, [defaultSelectedPlanId]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) ?? null,
    [plans, selectedPlanId]
  );

  const treatment = useMemo(() => {
    if (!selectedPlan) return null;
    return findTreatment(treatmentCatalog, selectedPlan.treatmentId) ?? null;
  }, [selectedPlan, treatmentCatalog]);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideApplied, setOverrideApplied] = useState(false);
  const [testPlanEnabled, setTestPlanEnabled] = useState(false);

  const [beforeStart, setBeforeStart] = useState<Dayjs | null>(null);
  const [beforeEnd, setBeforeEnd] = useState<Dayjs | null>(null);
  const [afterStart, setAfterStart] = useState<Dayjs | null>(null);
  const [afterEnd, setAfterEnd] = useState<Dayjs | null>(null);

  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    setOverrideApplied(false);
    setOverrideOpen(false);
    setTestPlanEnabled(false);

    const anchor = dayjs(
      selectedPlan?.startDate ?? dayjs().format('YYYY-MM-DD'),
      'YYYY-MM-DD',
      true
    );

    setBeforeEnd(anchor);
    setBeforeStart(anchor.subtract(12, 'week'));
    setAfterStart(anchor);
    setAfterEnd(anchor.add(12, 'week'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanId]);

  useEffect(() => {
    if (testPlanEnabled) {
      setNormalWindows({
        overrideApplied,
        overrideOpen,
        beforeStart,
        beforeEnd,
        afterStart,
        afterEnd,
      });

      setOverrideOpen(false);
      setOverrideApplied(true);

      const today = dayjs().startOf('day');
      const aEnd = today;
      const aStart = today.subtract(6, 'day');
      const bEnd = aStart.subtract(1, 'day');
      const bStart = bEnd.subtract(6, 'day');

      setAfterStart(aStart);
      setAfterEnd(aEnd);
      setBeforeStart(bStart);
      setBeforeEnd(bEnd);
      return;
    }

    if (normalWindows) {
      setOverrideApplied(normalWindows.overrideApplied);
      setOverrideOpen(normalWindows.overrideOpen);
      setBeforeStart(normalWindows.beforeStart);
      setBeforeEnd(normalWindows.beforeEnd);
      setAfterStart(normalWindows.afterStart);
      setAfterEnd(normalWindows.afterEnd);
      setNormalWindows(null);
    } else {
      setOverrideApplied(false);

      const anchor = dayjs(
        selectedPlan?.startDate ?? dayjs().format('YYYY-MM-DD'),
        'YYYY-MM-DD',
        true
      );

      setBeforeEnd(anchor);
      setBeforeStart(anchor.subtract(12, 'week'));
      setAfterStart(anchor);
      setAfterEnd(anchor.add(12, 'week'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testPlanEnabled]);

  const canApplyOverride =
    !testPlanEnabled &&
    !!beforeStart &&
    !!beforeEnd &&
    !!afterStart &&
    !!afterEnd &&
    beforeStart.isValid() &&
    beforeEnd.isValid() &&
    afterStart.isValid() &&
    afterEnd.isValid() &&
    (beforeStart.isBefore(beforeEnd) || beforeStart.isSame(beforeEnd, 'day')) &&
    (afterStart.isBefore(afterEnd) || afterStart.isSame(afterEnd, 'day'));

  const currentParams = useMemo<TreatmentEffectParams | null>(() => {
    if (!selectedPlan || !beforeStart || !beforeEnd || !afterStart || !afterEnd)
      return null;

    const bs = beforeStart.format('YYYY-MM-DD');
    const be = beforeEnd.format('YYYY-MM-DD');
    const as = afterStart.format('YYYY-MM-DD');
    const ae = afterEnd.format('YYYY-MM-DD');

    const mode: 'default' | 'override' =
      testPlanEnabled || overrideApplied ? 'override' : 'default';

    return {
      planId: selectedPlan.id,
      treatmentId: selectedPlan.treatmentId,
      mode,
      anchorDate: selectedPlan.startDate,
      beforeStart: bs,
      beforeEnd: be,
      afterStart: as,
      afterEnd: ae,
    };
  }, [
    selectedPlan,
    beforeStart,
    beforeEnd,
    afterStart,
    afterEnd,
    overrideApplied,
    testPlanEnabled,
  ]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!patient?._id || !selectedPlan || !currentParams) return;

      setEffectLoading(true);
      setEffectError(null);

      try {
        const resp = await fetchTreatmentEffect(patient._id, currentParams);
        if (!cancelled) setEffect(resp.data.result);
      } catch (e: unknown) {
        reportError(e);
        if (!cancelled) {
          const error = e as ApiError;
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            t('treatmentEffect.failedToLoad');
          setEffectError(errorMessage);
          setEffect(null);
        }
      } finally {
        if (!cancelled) setEffectLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [patient?._id, selectedPlanId, currentParams, selectedPlan, t]);

  if (plansLoading && !plans.length) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('treatmentEffect.loadingInline')}
        </Alert>
      </Box>
    );
  }

  if (plansError && !plans.length) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {plansError}
        </Alert>
      </Box>
    );
  }

  if (!plans.length) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Typography sx={{ mt: 1 }}>
          {t('treatmentEffect.noTreatmentPlans')}
        </Typography>
      </Box>
    );
  }

  if (!selectedPlan) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('treatmentEffect.missingPlanOrTreatment')}
        </Alert>
      </Box>
    );
  }

  if (treatmentsLoading && !treatmentCatalog.length) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('common.loading')}
        </Alert>
      </Box>
    );
  }

  if (treatmentsError && !treatmentCatalog.length) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {treatmentsError}
        </Alert>
      </Box>
    );
  }

  if (!treatment) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('treatmentEffect.missingPlanOrTreatment')}
        </Alert>
      </Box>
    );
  }

  if (effectLoading && !effect) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('treatmentEffect.loadingInline')}
        </Alert>
      </Box>
    );
  }

  if (effectError && !effect) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {effectError}
        </Alert>
      </Box>
    );
  }

  if (!effect) {
    return (
      <Box sx={profileBoxStyle}>
        <Typography
          sx={{ fontFamily: GENERAL_FONT, fontWeight: 900, fontSize: 18 }}
        >
          {t('treatmentEffect.title')}
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('treatmentEffect.noData')}
        </Alert>
      </Box>
    );
  }

  const { before } = effect.windows;
  const { after } = effect.windows;

  const headerBeforeRange =
    overrideApplied && beforeStart && beforeEnd
      ? formatRange(
          beforeStart.format('YYYY-MM-DD'),
          beforeEnd.format('YYYY-MM-DD')
        )
      : formatRange(before.startDate, before.endDate);

  const headerAfterRange =
    overrideApplied && afterStart && afterEnd
      ? formatRange(
          afterStart.format('YYYY-MM-DD'),
          afterEnd.format('YYYY-MM-DD')
        )
      : formatRange(after.startDate, after.endDate);

  const anchorSourceLabel =
    effect.plan.anchorSource === 'patientConfirmedStartDate'
      ? t('treatmentEffect.anchorSourceStartDate')
      : t('treatmentEffect.anchorSourceActivatedAt');

  const defaultWindowLabel = `${t('treatmentEffect.defaultWindowPrefix')} 12 ${t(
    'treatmentEffect.weeks'
  )}`;

  const getWindowModeLabel = () => {
    if (testPlanEnabled) {
      return t('treatmentEffect.testPlanActive');
    }
    if (overrideApplied) {
      return t('treatmentEffect.manualOverrideActive');
    }
    return `${t('treatmentEffect.usingDefaultWindows')} (${defaultWindowLabel})`;
  };

  const renderDataQuality = (w: EffectWindow) => {
    const weeksAvail = weeksDataAvailable(w.diaryDays);
    return (
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
        <Chip
          size="small"
          variant="outlined"
          label={`${t('treatmentEffect.diaryDays')}: ${w.diaryDays}/${w.totalDays}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${t('treatmentEffect.dataAvailable')}: ${weeksAvail} ${t('treatmentEffect.weeks')}`}
        />
        <Chip
          size="small"
          color={coverageColor(w.coverage)}
          variant="outlined"
          label={`${t('treatmentEffect.coverage')}: ${pct(w.coverage * 100)}`}
        />
      </Stack>
    );
  };

  const multipleActive = plans.filter((p) => p.status === 'active').length > 1;

  return (
    <Box sx={{ ...profileBoxStyle, p: 2.25 }}>
      <Alert severity="error" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {t('treatmentEffect.testVersionDisclaimer')}
        </Typography>
      </Alert>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              sx={{
                fontFamily: GENERAL_FONT,
                fontWeight: 950,
                fontSize: 18,
                lineHeight: 1.1,
              }}
            >
              {t('treatmentEffect.title')}
            </Typography>

            <IconButton
              size="small"
              onClick={() => setInfoOpen(true)}
              aria-label={t('treatmentEffect.infoAriaLabel')}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>

            {multipleActive && (
              <Chip
                size="small"
                color="warning"
                label={t('treatmentEffect.multipleActiveWarning')}
              />
            )}

            {plansLoading && (
              <Chip
                size="small"
                variant="outlined"
                label={t('treatmentEffect.loadingInline')}
              />
            )}
            {effectLoading && (
              <Chip
                size="small"
                variant="outlined"
                label={t('treatmentEffect.loadingInline')}
              />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.75 }}>
            <Typography
              sx={{ fontFamily: GENERAL_FONT, opacity: 0.7, fontSize: 13 }}
            >
              {treatment.displayName} · {t('treatmentEffect.startDate')}:{' '}
              {selectedPlan.startDate}
              {selectedPlan.endDate
                ? ` · ${t('treatmentEffect.endDate')}: ${selectedPlan.endDate}`
                : ''}
            </Typography>
            {statusChipSv(selectedPlan.status)}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ mt: 0.75, flexWrap: 'wrap' }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={getWindowModeLabel()}
            />

            <Typography
              sx={{ fontFamily: GENERAL_FONT, opacity: 0.75, fontSize: 13 }}
            >
              {t('treatmentEffect.anchor')}: {effect.plan.anchorDate} (
              {anchorSourceLabel})
            </Typography>

            <Typography
              sx={{ fontFamily: GENERAL_FONT, opacity: 0.75, fontSize: 13 }}
            >
              {t('treatmentEffect.before')}: {headerBeforeRange}
            </Typography>

            <Typography
              sx={{ fontFamily: GENERAL_FONT, opacity: 0.75, fontSize: 13 }}
            >
              {t('treatmentEffect.after')}: {headerAfterRange}
            </Typography>
          </Stack>

          {(plansError || effectError) && (
            <Alert severity="warning" sx={{ mt: 1.25 }}>
              {plansError || effectError}
            </Alert>
          )}
        </Box>

        <FormControl size="small" sx={{ minWidth: 380 }}>
          <InputLabel>{t('treatmentEffect.treatmentPlan')}</InputLabel>
          <Select
            label={t('treatmentEffect.treatmentPlan')}
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(String(e.target.value))}
          >
            {plans.map((p) => {
              const tr = findTreatment(treatmentCatalog, p.treatmentId);
              const label = tr?.displayName ?? p.treatmentId;
              return (
                <MenuItem key={p.id} value={p.id}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%' }}
                    gap={2}
                  >
                    <Typography sx={{ fontFamily: GENERAL_FONT, fontSize: 14 }}>
                      {label} · {p.startDate}
                      {p.endDate ? ` → ${p.endDate}` : ''}
                    </Typography>
                    {statusChipSv(p.status)}
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Alert severity="info" sx={{ mb: 1.25 }}>
        {t('treatmentEffect.missingDaysNote')}
      </Alert>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography
          sx={{
            fontFamily: GENERAL_FONT,
            fontWeight: 800,
            fontSize: 14,
            opacity: 0.9,
          }}
        >
          {t('treatmentEffect.comparisonWindow')}
        </Typography>

        <Stack direction="row" gap={1} alignItems="center">
          <FormControlLabel
            sx={{ mr: 0.5 }}
            control={
              <Switch
                size="small"
                checked={testPlanEnabled}
                onChange={(e) => setTestPlanEnabled(e.target.checked)}
              />
            }
            label={t('treatmentEffect.testPlanToggle')}
          />

          <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none' }}
            disabled={testPlanEnabled}
            onClick={() => setOverrideOpen((v) => !v)}
          >
            {overrideOpen
              ? t('treatmentEffect.hideOverride')
              : t('treatmentEffect.showOverride')}
          </Button>

          <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none' }}
            disabled={testPlanEnabled || !overrideApplied}
            onClick={() => setOverrideApplied(false)}
          >
            {t('treatmentEffect.useDefault')}
          </Button>
        </Stack>
      </Stack>

      <Collapse in={overrideOpen}>
        <Card variant="outlined" sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={1.25}>
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      fontWeight: 800,
                      fontSize: 13,
                      mb: 0.75,
                    }}
                  >
                    {t('treatmentEffect.before')}
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <DatePicker
                      slotProps={{
                        textField: { size: 'small', fullWidth: true },
                      }}
                      value={beforeStart}
                      onChange={(v) => setBeforeStart(v)}
                      label={t('from')}
                      format="YYYY-MM-DD"
                      maxDate={beforeEnd ?? undefined}
                    />
                    <DatePicker
                      slotProps={{
                        textField: { size: 'small', fullWidth: true },
                      }}
                      value={beforeEnd}
                      onChange={(v) => setBeforeEnd(v)}
                      label={t('to')}
                      format="YYYY-MM-DD"
                      minDate={beforeStart ?? undefined}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      fontWeight: 800,
                      fontSize: 13,
                      mb: 0.75,
                    }}
                  >
                    {t('treatmentEffect.after')}
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <DatePicker
                      slotProps={{
                        textField: { size: 'small', fullWidth: true },
                      }}
                      value={afterStart}
                      onChange={(v) => setAfterStart(v)}
                      label={t('from')}
                      format="YYYY-MM-DD"
                      maxDate={afterEnd ?? undefined}
                    />
                    <DatePicker
                      slotProps={{
                        textField: { size: 'small', fullWidth: true },
                      }}
                      value={afterEnd}
                      onChange={(v) => setAfterEnd(v)}
                      label={t('to')}
                      format="YYYY-MM-DD"
                      minDate={afterStart ?? undefined}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    gap={1}
                    sx={{ mt: 0.5 }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ textTransform: 'none' }}
                      onClick={() => {
                        setOverrideApplied(true);
                        setOverrideOpen(false);
                      }}
                      disabled={!canApplyOverride}
                    >
                      {t('treatmentEffect.applyOverride')}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </CardContent>
        </Card>
      </Collapse>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.75 }}>
              <Typography
                sx={{ fontFamily: GENERAL_FONT, fontWeight: 950, fontSize: 14 }}
              >
                {t('treatmentEffect.beforeTreatment')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: GENERAL_FONT,
                  opacity: 0.7,
                  fontSize: 12.5,
                  mt: 0.5,
                }}
              >
                {headerBeforeRange}
              </Typography>

              <Grid container spacing={1} sx={{ mt: 0.75 }}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      opacity: 0.75,
                      fontSize: 12.5,
                    }}
                  >
                    {t('treatmentEffect.avgHeadacheDaysPerMonth')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      fontSize: 26,
                      fontWeight: 950,
                      lineHeight: 1.05,
                    }}
                  >
                    {num1(before.avgHeadacheDaysPerMonth)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      opacity: 0.75,
                      fontSize: 12.5,
                    }}
                  >
                    {t('treatmentEffect.avgMigraineDaysPerMonth')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      fontSize: 26,
                      fontWeight: 950,
                      lineHeight: 1.05,
                    }}
                  >
                    {num1(before.avgMigraineDaysPerMonth)}
                  </Typography>
                </Grid>
              </Grid>

              {renderDataQuality(before)}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.75 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  sx={{
                    fontFamily: GENERAL_FONT,
                    fontWeight: 950,
                    fontSize: 14,
                  }}
                >
                  {t('treatmentEffect.afterTreatment')}
                </Typography>
                {statusChipSv(effect.plan.status)}
              </Stack>

              <Typography
                sx={{
                  fontFamily: GENERAL_FONT,
                  opacity: 0.7,
                  fontSize: 12.5,
                  mt: 0.5,
                }}
              >
                {headerAfterRange}
              </Typography>

              <Grid container spacing={1} sx={{ mt: 0.75 }}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      opacity: 0.75,
                      fontSize: 12.5,
                    }}
                  >
                    {t('treatmentEffect.avgHeadacheDaysPerMonth')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      fontSize: 26,
                      fontWeight: 950,
                      lineHeight: 1.05,
                    }}
                  >
                    {num1(after.avgHeadacheDaysPerMonth)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      opacity: 0.75,
                      fontSize: 12.5,
                    }}
                  >
                    {t('treatmentEffect.avgMigraineDaysPerMonth')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: GENERAL_FONT,
                      fontSize: 26,
                      fontWeight: 950,
                      lineHeight: 1.05,
                    }}
                  >
                    {num1(after.avgMigraineDaysPerMonth)}
                  </Typography>
                </Grid>
              </Grid>

              {renderDataQuality(after)}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={1.5}
            alignItems="stretch"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.75 }}>
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: GENERAL_FONT,
                          fontWeight: 950,
                          fontSize: 14,
                        }}
                      >
                        {t('treatmentEffect.migraineReduction')}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: GENERAL_FONT,
                          fontSize: 34,
                          fontWeight: 950,
                          lineHeight: 1.0,
                          color:
                            effect.derived.migraineReductionPct < 0
                              ? 'error.main'
                              : 'success.main',
                        }}
                      >
                        {pct(effect.derived.migraineReductionPct)}
                      </Typography>

                      {typeof effect.derived.headacheReductionPct ===
                        'number' && (
                        <Typography
                          sx={{
                            fontFamily: GENERAL_FONT,
                            opacity: 0.8,
                            fontSize: 13,
                            mt: 0.75,
                          }}
                        >
                          {t('treatmentEffect.headacheReduction')}:{' '}
                          {pct(effect.derived.headacheReductionPct)}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Box
              sx={{
                width: { xs: '100%', md: 260 },
                flexShrink: 0,
                alignSelf: { md: 'stretch' },
              }}
            >
              <Stack
                gap={1}
                sx={{ position: { md: 'sticky' }, top: { md: 12 } }}
              >
                <CombinedCopyToEmrButton
                  disabled={!effect}
                  effect={effect}
                  selectedPlan={selectedPlan}
                  plans={plans}
                  treatmentCatalog={treatmentCatalog}
                  selectedTreatmentName={treatment.displayName}
                  beforeRangeLabel={headerBeforeRange}
                  afterRangeLabel={headerAfterRange}
                />
              </Stack>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: GENERAL_FONT, fontWeight: 900 }}>
          {treatment.displayName}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.25}>
            <Grid item xs={12} md={6}>
              <Typography
                sx={{ fontFamily: GENERAL_FONT, opacity: 0.7, fontSize: 12.5 }}
              >
                {t('treatmentEffect.treatmentClass')}
              </Typography>
              <Typography sx={{ fontFamily: GENERAL_FONT, fontWeight: 800 }}>
                {treatment.treatmentClass}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                sx={{ fontFamily: GENERAL_FONT, opacity: 0.7, fontSize: 12.5 }}
              >
                {t('treatmentEffect.activeSubstance')}
              </Typography>
              <Typography sx={{ fontFamily: GENERAL_FONT, fontWeight: 800 }}>
                {treatment.activeSubstance ?? t('treatmentEffect.notAvailable')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography
                sx={{ fontFamily: GENERAL_FONT, opacity: 0.7, fontSize: 12.5 }}
              >
                {t('treatmentEffect.aliases')}
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {treatment.aliases.map((a) => (
                  <Chip key={a} size="small" variant="outlined" label={a} />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ textTransform: 'none' }}
            onClick={() => setInfoOpen(false)}
          >
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TreatmentEffectDashboard;
